import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updatePassword } from 'aws-amplify/auth';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Alert } from '../components/ui/Alert';

export default function ChangePassword() {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);
    try {
      await updatePassword({ oldPassword: currentPassword, newPassword });
      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      if (err.name === 'NotAuthorizedException') {
        setError('Current password is incorrect');
      } else if (err.name === 'InvalidPasswordException') {
        setError('New password does not meet requirements (min 8 chars, uppercase, lowercase, number, special character)');
      } else {
        setError(err.message || 'Failed to change password');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-md mx-auto mt-8">
        <Card>
          <CardBody className="text-center py-8">
            <div className="mb-4"><CheckCircleIcon className="h-10 w-10 mx-auto" style={{ color: 'var(--green-600)' }} /></div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Password Changed!</h2>
            <p className="text-gray-600">Your password has been updated. Redirecting...</p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-8">
      <Card>
        <CardBody>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Change Password</h2>
          <p className="text-sm text-gray-500 mb-6">Update your account password.</p>

          {error && <Alert variant="error" className="mb-4" onClose={() => setError('')}>{error}</Alert>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Current Password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
            <Input
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              placeholder="Min 8 characters"
              autoComplete="new-password"
            />
            <Input
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
            />

            <div className="text-xs text-gray-400 space-y-0.5">
              <p>Password requirements:</p>
              <ul className="list-disc pl-4">
                <li>At least 8 characters</li>
                <li>Uppercase and lowercase letters</li>
                <li>At least one number</li>
                <li>At least one special character (!@#$%^&*)</li>
              </ul>
            </div>

            <div className="flex justify-between pt-2">
              <Button type="button" variant="secondary" onClick={() => navigate(-1)}>Cancel</Button>
              <Button type="submit" loading={loading}>Change Password</Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
